/**
 * Typed, defensive wrapper around the Telegram Mini App SDK
 * (https://core.telegram.org/bots/webapps). Every helper degrades
 * gracefully so the app also runs in a normal browser during development.
 */

type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type HapticNotification = 'error' | 'success' | 'warning';

export type InvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { id: number; first_name: string } };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string | undefined>;
  ready(): void;
  expand(): void;
  close(): void;
  isVersionAtLeast(version: string): boolean;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  disableVerticalSwipes?(): void;
  enableClosingConfirmation?(): void;
  disableClosingConfirmation?(): void;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;
  openTelegramLink(url: string): void;
  openLink(url: string): void;
  showConfirm(message: string, callback: (ok: boolean) => void): void;
  showAlert(message: string, callback?: () => void): void;
  requestWriteAccess?(callback?: (granted: boolean) => void): void;
  openInvoice?(url: string, callback?: (status: InvoiceStatus) => void): void;
  /** Bot API 8.0+: native "save file" prompt. */
  downloadFile?(params: { url: string; file_name: string }, callback?: (accepted: boolean) => void): void;
  BackButton: {
    isVisible: boolean;
    show(): void;
    hide(): void;
    onClick(handler: () => void): void;
    offClick(handler: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: HapticStyle): void;
    notificationOccurred(type: HapticNotification): void;
    selectionChanged(): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg: TelegramWebApp | undefined = window.Telegram?.WebApp;

/** True only when actually launched inside Telegram (initData is present). */
export const isInTelegram = (): boolean => Boolean(tg?.initData);

const supports = (version: string) => Boolean(tg?.isVersionAtLeast?.(version));

export function initTelegram(): void {
  applyColorScheme();
  if (!tg) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', applyColorScheme);
    return;
  }
  tg.ready();
  tg.expand();
  if (supports('7.7')) tg.disableVerticalSwipes?.();
  tg.onEvent('themeChanged', applyColorScheme);
}

/** Follows Telegram's light/dark mode, falling back to the OS preference. */
function applyColorScheme(): void {
  const scheme = isInTelegram()
    ? tg!.colorScheme
    : window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  document.documentElement.dataset.theme = scheme;
  if (tg && supports('6.1')) {
    const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
    if (bg) {
      tg.setHeaderColor(bg);
      tg.setBackgroundColor(bg);
    }
  }
}

/** Opens a page in the browser (Telegram's in-app one) without leaving the Mini App. */
export function openPage(url: string): void {
  const absolute = new URL(url, window.location.origin).href;
  if (tg?.openLink) tg.openLink(absolute);
  else window.open(absolute, '_blank', 'noopener');
}

/**
 * The launch parameter from `t.me/<bot>/<app>?startapp=...`, or from the
 * `?startapp=` query string used by bot inline buttons and local testing.
 */
export function getStartParam(): string | undefined {
  return tg?.initDataUnsafe?.start_param ?? new URLSearchParams(window.location.search).get('startapp') ?? undefined;
}

/**
 * Asks the user (native Telegram prompt) to let the bot send them direct
 * messages: notifications and payment requests. Resolves false outside
 * Telegram or if the user declines.
 */
export function requestBotMessages(): Promise<boolean> {
  if (!tg?.requestWriteAccess || !supports('6.9')) return Promise.resolve(false);
  return new Promise((resolve) => tg.requestWriteAccess!((granted) => resolve(Boolean(granted))));
}

export const haptic = {
  tap: () => tg?.HapticFeedback?.impactOccurred('light'),
  select: () => tg?.HapticFeedback?.selectionChanged(),
  success: () => tg?.HapticFeedback?.notificationOccurred('success'),
  error: () => tg?.HapticFeedback?.notificationOccurred('error'),
  warning: () => tg?.HapticFeedback?.notificationOccurred('warning'),
};

export function confirmDialog(message: string): Promise<boolean> {
  if (tg && supports('6.2')) {
    return new Promise((resolve) => tg.showConfirm(message, resolve));
  }
  return Promise.resolve(window.confirm(message));
}

/** Opens Telegram's native "share to chat" picker, or the Web Share API outside Telegram. */
export async function shareLink(url: string, text: string): Promise<'shared' | 'copied'> {
  if (tg) {
    tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`);
    return 'shared';
  }
  if (navigator.share) {
    try {
      await navigator.share({ url, text });
      return 'shared';
    } catch {
      // User cancelled; fall through to copying.
    }
  }
  await navigator.clipboard.writeText(url);
  return 'copied';
}

export const backButton = {
  show(handler: () => void): () => void {
    if (!tg || !supports('6.1')) return () => undefined;
    tg.BackButton.onClick(handler);
    tg.BackButton.show();
    return () => {
      tg.BackButton.offClick(handler);
      tg.BackButton.hide();
    };
  },
};

/**
 * Opens a Telegram Stars invoice inside Telegram and resolves with how it
 * ended. 'unavailable' outside Telegram (payments only work in the app).
 */
export function payWithStars(invoiceLink: string): Promise<InvoiceStatus | 'unavailable'> {
  return new Promise((resolve) => {
    if (!tg?.openInvoice || !isInTelegram()) return resolve('unavailable');
    tg.openInvoice(invoiceLink, (status) => resolve(status));
  });
}

/**
 * Saves an image (e.g. a KHQR) to the phone so it can be paid from a bank
 * app's "scan from photo": Telegram's native download where available, else
 * the share sheet ("Save Image"), else a browser download.
 */
export async function saveImage(url: string, fileName: string): Promise<'saved' | 'cancelled'> {
  if (tg?.downloadFile && tg.isVersionAtLeast('8.0')) {
    return new Promise((resolve) =>
      tg!.downloadFile!({ url, file_name: fileName }, (accepted) => resolve(accepted ? 'saved' : 'cancelled')),
    );
  }
  const blob = await (await fetch(url)).blob();
  const file = new File([blob], fileName, { type: blob.type || 'image/png' });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return 'saved';
    } catch {
      return 'cancelled';
    }
  }
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
  return 'saved';
}
